import React from 'react';
import { Database, Info, Code, Lock, Users, GitBranch, Workflow, ChevronDown, Trash2 } from 'lucide-react';
import { NavLink } from 'react-router-dom';
import { cn } from '../lib/utils';
import { useAuth } from '../contexts/AuthContext';

const DEVELOPER_TABS = [
  {
    id: 'overview',
    label: 'Overview',
    icon: Info,
    href: '/developer/overview',
  },
  {
    id: 'api',
    label: 'API',
    icon: Code,
    href: '/developer/api',
  },
  {
    id: 'database',
    label: 'Database',
    icon: Database,
    href: '/developer/database',
  },
  {
    id: 'auth',
    label: 'Auth',
    icon: Lock,
    href: '/developer/auth',
  },
  {
    id: 'roles',
    label: 'Roles',
    icon: Users,
    href: '/developer/roles',
  },
  {
    id: 'logic',
    label: 'Logic',
    icon: Workflow,
    href: '/developer/logic',
  },
  {
    id: 'user-management',
    label: 'User Management',
    icon: Users,
    href: '/developer/user-management',
  },
  {
    id: 'architecture',
    label: 'Architecture',
    icon: GitBranch,
    href: '/developer/architecture',
  },
  {
    id: 'demo',
    label: 'Demo',
    icon: Trash2,
    href: '/developer/demo',
  },
];

interface DeveloperMenuProps {
  expanded: boolean;
  onToggle: () => void;
}

export default function DeveloperMenu({ expanded, onToggle }: DeveloperMenuProps) {
  const { user } = useAuth();

  // Only show Developer menu to app_owner, saas_admin, and saas_developer
  const isAuthorized = user?.role === 'app_owner' || user?.role === 'saas_admin' || user?.role === 'saas_developer';

  // Return null in the render phase, not after hooks
  if (!isAuthorized) {
    return null;
  }

  return (
    <div className="mt-4 border-t border-gray-200 pt-4">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium text-purple-700 hover:bg-purple-50 transition-colors"
      >
        <span className="flex items-center gap-3">
          <Code className="h-5 w-5" />
          Developer
        </span>
        <ChevronDown
          className={cn(
            'h-4 w-4 transition-transform',
            expanded && 'rotate-180'
          )}
        />
      </button>

      {expanded && (
        <div className="mt-2 space-y-1">
          {DEVELOPER_TABS.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.id}
                to={item.href}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ml-4 mb-1',
                    isActive
                      ? 'bg-purple-50 text-purple-700'
                      : 'text-gray-600 hover:bg-gray-100'
                  )
                }
              >
                <Icon className="h-4 w-4" />
                {item.label}
              </NavLink>
            );
          })}
        </div>
      )}
    </div>
  );
}

